import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { Paginated } from "../../common/types/Paginated";
import {
  CoreUserType,
  ExpertUser,
  HrExpertUser,
} from "./types/entities";
import {
  createExpertUser,
  getCoreUsers,
  hrExpertById,
  updateExpertOutOfOffice as updateExpertOutOfOfficeRequest,
  updateExpertUser,
  updateExpertUserEnabledState,
} from "./userAPI";
import {
  CreateExpertUser,
  UpdateExpertOutOfOffice,
  UpdateExpertUser,
  UpdateExpertUserEnabledState,
} from "./types/commands";
import {
  ExpertOutOfOfficeChanged,
  ExpertUserCreated,
  ExpertUserUpdated,
} from "./types/events";
import { EventPayload } from "../../common/types/Event";
import { getCoreUserFullName } from "../../common/utils/users";
import {
  createCommandAsyncThunk,
  createQueryAsyncThunk,
} from "../../app/createApiAsyncThunk";

export type SliceState = {
  ids: string[];
  entities: {
    [id: string]: ExpertUser;
  };
  status: "idle" | "pending" | "succeeded" | "failed";
  lastFetched: number | null;
};

export const shouldFetchExpertUsers = (state: {
  lastFetched: number | null;
  status: SliceState["status"];
}): boolean => {
  if (state.status === "pending") return false;
  if (state.lastFetched != null && state.status !== "failed") return false;
  return true;
};

export const fetchExpertUsers = createAsyncThunk<
  Paginated<ExpertUser[]>,
  {},
  { state: RootState }
>(
  "expertUsers/fetchExpertUsers",
  async (_, { requestId }) =>
    getCoreUsers({ type: CoreUserType.EXPERT }, { requestId }) as Promise<
      Paginated<ExpertUser[]>
    >,
  {
    condition: (_, { getState }) =>
      shouldFetchExpertUsers(getState().expertUsers),
  },
);

export const fetchExpertUsersByIds = createQueryAsyncThunk<
  Paginated<ExpertUser[]>,
  { userIds: string[] }
>(
  "expertUsers/fetchExpertUsersByIds",
  (filters, options) =>
    getCoreUsers(filters, options) as Promise<Paginated<ExpertUser[]>>,
);

export const fetchHrExpertUser = createAsyncThunk<
  HrExpertUser,
  { id: string },
  { state: RootState }
>("expertUsers/fetchHrExpertUsers", async ({ id }, { requestId }) =>
  hrExpertById(id, { requestId }),
);

export const createExpert = createAsyncThunk<
  EventPayload<ExpertUserCreated>[],
  CreateExpertUser,
  { state: RootState }
>("expertUsers/createExpert", async (payload: CreateExpertUser) => {
  const res = await createExpertUser(payload);
  return res;
});

export const updateExpert = createCommandAsyncThunk<
  UpdateExpertUser,
  ExpertUserUpdated
>("expertUsers/updateExpert", updateExpertUser);

export const updateExpertEnabledState = createAsyncThunk<
  EventPayload<{}>[],
  UpdateExpertUserEnabledState,
  { state: RootState }
>(
  "expertUsers/updateExpertEnabledState",
  async (payload: UpdateExpertUserEnabledState) => {
    return updateExpertUserEnabledState(payload);
  },
);

export const updateExpertOutOfOffice = createCommandAsyncThunk<
  UpdateExpertOutOfOffice,
  ExpertOutOfOfficeChanged
>("expertUsers/updateExpertOutOfOffice", updateExpertOutOfOfficeRequest);

const expertUsersAdapter = createEntityAdapter<ExpertUser>();

export const expertUsersSlice = createSlice({
  name: "expertUsers",
  initialState: expertUsersAdapter.getInitialState({
    status: "idle",
    lastFetched: null,
  }) as SliceState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchExpertUsers.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchExpertUsers.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchExpertUsers.fulfilled, (state, action) => {
      expertUsersAdapter.setAll(state, action.payload.data);
      state.status = "succeeded";
      state.lastFetched = Date.now();
    });
    builder.addCase(fetchExpertUsersByIds.fulfilled, (state, action) => {
      expertUsersAdapter.upsertMany(state, action.payload.data);
    });
    builder.addCase(createExpert.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(createExpert.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(createExpert.fulfilled, (state, action) => {
      state.status = "succeeded";
      if (action.payload.length === 0) return;
      let expert: ExpertUser = {
        id: "",
        type: "",
        account: {
          ...action.meta.arg,
          registered: false,
          orderQueueIds: [],
          version: 0,
        },
      };
      action.payload.forEach((event) => {
        expert = {
          ...expert,
          id: event.id,
          account: { ...expert.account, version: event.version },
        };
      });
      expertUsersAdapter.addOne(state, expert as ExpertUser);
    });
    builder.addCase(updateExpert.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateExpert.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateExpert.fulfilled, (state, action) => {
      state.status = "succeeded";
      const { expertId } = action.meta.arg;
      const expert = state.entities[expertId];
      const updatedExpert = {
        ...expert,
        account: {
          ...expert.account,
          version: action.payload[0].version,
          authScopes: action.meta.arg.authScopes,
          eezyCompanyIds: action.meta.arg.eezyCompanyIds,
        },
      };
      expertUsersAdapter.updateOne(state, {
        id: expertId,
        changes: updatedExpert,
      });
    });
    builder.addCase(updateExpertEnabledState.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateExpertEnabledState.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateExpertEnabledState.fulfilled, (state, action) => {
      state.status = "succeeded";
      const { expertId } = action.meta.arg;
      const expert = state.entities[expertId];
      const updatedExpert = {
        ...expert,
        account: {
          ...expert.account,
          version: action.payload[0].version,
          enabled: action.meta.arg.enabled,
        },
      };
      expertUsersAdapter.updateOne(state, {
        id: expertId,
        changes: updatedExpert,
      });
    });
    builder.addCase(updateExpertOutOfOffice.fulfilled, (state, action) => {
      const version = action.payload[0]?.version;
      if (version == null) return;

      const { expertId, message, from, until } = action.meta.arg;
      const expert = state.entities[expertId];
      if (!expert) return;

      expertUsersAdapter.updateOne(state, {
        id: expertId,
        changes: {
          account: {
            ...expert.account,
            outOfOffice:
              from && until
                ? { message: message ?? "", from, until, version }
                : { version },
          },
        },
      });
    });
  },
});

export const selectExpertUserOptions = createSelector(
  (state: RootState) => state.expertUsers.entities,
  (entities) =>
    Object.values(entities).map((expertUser) => ({
      id: expertUser.id,
      name: getCoreUserFullName(expertUser),
    })),
);

export const selectActiveExpertUserOptions = createSelector(
  (state: RootState) => state.expertUsers.entities,
  (entities) =>
    Object.values(entities)
      .filter((e) => e.account.enabled == null || e.account.enabled === true)
      .map((expertUser) => ({
        id: expertUser.id,
        name: getCoreUserFullName(expertUser),
      }))
      .sort((a, b) => a.name.localeCompare(b.name)),
);

export const {
  selectAll: selectAllExpertUsers,
  selectById: selectExpertUserById,
} = expertUsersAdapter.getSelectors((state: RootState) => state.expertUsers);

export const expertUsersReducer = expertUsersSlice.reducer;
export default expertUsersReducer;
